import type { TKey } from '@/i18n/msg';

/**
 * “UTC-4:00”, “UTC+5:30”, “UTC+0:00” — the zone’s offset now, as a
 * locale-neutral label (#2109 R3 L7). Computed from the zone’s wall-clock
 * numbers rather than parsed out of an English `shortOffset` name, so no
 * locale is hard-coded and every UI language shows the same notation.
 */
export function getUtcOffset(timezone: string, now: Date = new Date()): string {
  try {
    // Only the numeric Latin-digit parts are read, so the locale changes
    // nothing here; it is named so no browser locale leaks in (R3-B5 rule).
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: timezone,
      hourCycle: 'h23',
      year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric',
      numberingSystem: 'latn',
    }).formatToParts(now);
    const n = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find((p) => p.type === type)?.value);
    const wall = Date.UTC(n('year'), n('month') - 1, n('day'), n('hour'), n('minute'), n('second'));
    const minutes = Math.round((wall - Math.floor(now.getTime() / 1000) * 1000) / 60_000);
    if (!Number.isFinite(minutes)) return '';
    const sign = minutes < 0 ? '-' : '+';
    const abs = Math.abs(minutes);
    return `UTC${sign}${Math.floor(abs / 60)}:${String(abs % 60).padStart(2, '0')}`;
  } catch {
    return '';
  }
}

// #2109 R3-B4: labels are `settings:account.timezone.*` keys, resolved at render.
export const TIMEZONE_GROUPS: { labelKey: TKey; zones: { value: string; labelKey: TKey }[] }[] = [
  {
    labelKey: 'settings:account.timezone.groups.us',
    zones: [
      { value: 'America/New_York', labelKey: 'settings:account.timezone.zones.newYork' },
      { value: 'America/Chicago', labelKey: 'settings:account.timezone.zones.chicago' },
      { value: 'America/Denver', labelKey: 'settings:account.timezone.zones.denver' },
      { value: 'America/Phoenix', labelKey: 'settings:account.timezone.zones.phoenix' },
      { value: 'America/Los_Angeles', labelKey: 'settings:account.timezone.zones.losAngeles' },
      { value: 'America/Anchorage', labelKey: 'settings:account.timezone.zones.anchorage' },
      { value: 'Pacific/Honolulu', labelKey: 'settings:account.timezone.zones.honolulu' },
      { value: 'America/Halifax', labelKey: 'settings:account.timezone.zones.halifax' },
      { value: 'America/St_Johns', labelKey: 'settings:account.timezone.zones.stJohns' },
    ],
  },
  {
    labelKey: 'settings:account.timezone.groups.latinAmerica',
    zones: [
      { value: 'America/Mexico_City', labelKey: 'settings:account.timezone.zones.mexicoCity' },
      { value: 'America/Bogota', labelKey: 'settings:account.timezone.zones.bogota' },
      { value: 'America/Caracas', labelKey: 'settings:account.timezone.zones.caracas' },
      { value: 'America/Sao_Paulo', labelKey: 'settings:account.timezone.zones.saoPaulo' },
      { value: 'America/Argentina/Buenos_Aires', labelKey: 'settings:account.timezone.zones.buenosAires' },
      { value: 'America/Santiago', labelKey: 'settings:account.timezone.zones.santiago' },
    ],
  },
  {
    labelKey: 'settings:account.timezone.groups.europe',
    zones: [
      { value: 'Europe/London', labelKey: 'settings:account.timezone.zones.london' },
      { value: 'Europe/Paris', labelKey: 'settings:account.timezone.zones.paris' },
      { value: 'Europe/Berlin', labelKey: 'settings:account.timezone.zones.berlin' },
      { value: 'Europe/Madrid', labelKey: 'settings:account.timezone.zones.madrid' },
      { value: 'Europe/Rome', labelKey: 'settings:account.timezone.zones.rome' },
      { value: 'Europe/Stockholm', labelKey: 'settings:account.timezone.zones.stockholm' },
      { value: 'Europe/Warsaw', labelKey: 'settings:account.timezone.zones.warsaw' },
      { value: 'Europe/Helsinki', labelKey: 'settings:account.timezone.zones.helsinki' },
      { value: 'Europe/Kiev', labelKey: 'settings:account.timezone.zones.kiev' },
      { value: 'Europe/Moscow', labelKey: 'settings:account.timezone.zones.moscow' },
    ],
  },
  {
    labelKey: 'settings:account.timezone.groups.africaMiddleEast',
    zones: [
      { value: 'Africa/Cairo', labelKey: 'settings:account.timezone.zones.cairo' },
      { value: 'Africa/Lagos', labelKey: 'settings:account.timezone.zones.lagos' },
      { value: 'Africa/Nairobi', labelKey: 'settings:account.timezone.zones.nairobi' },
      { value: 'Africa/Johannesburg', labelKey: 'settings:account.timezone.zones.johannesburg' },
      { value: 'Asia/Jerusalem', labelKey: 'settings:account.timezone.zones.jerusalem' },
      { value: 'Asia/Riyadh', labelKey: 'settings:account.timezone.zones.riyadh' },
      { value: 'Asia/Dubai', labelKey: 'settings:account.timezone.zones.dubai' },
      { value: 'Asia/Tehran', labelKey: 'settings:account.timezone.zones.tehran' },
    ],
  },
  {
    labelKey: 'settings:account.timezone.groups.asia',
    zones: [
      { value: 'Asia/Karachi', labelKey: 'settings:account.timezone.zones.karachi' },
      { value: 'Asia/Kolkata', labelKey: 'settings:account.timezone.zones.kolkata' },
      { value: 'Asia/Dhaka', labelKey: 'settings:account.timezone.zones.dhaka' },
      { value: 'Asia/Bangkok', labelKey: 'settings:account.timezone.zones.bangkok' },
      { value: 'Asia/Singapore', labelKey: 'settings:account.timezone.zones.singapore' },
      { value: 'Asia/Shanghai', labelKey: 'settings:account.timezone.zones.shanghai' },
      { value: 'Asia/Tokyo', labelKey: 'settings:account.timezone.zones.tokyo' },
      { value: 'Asia/Seoul', labelKey: 'settings:account.timezone.zones.seoul' },
    ],
  },
  {
    labelKey: 'settings:account.timezone.groups.pacific',
    zones: [
      { value: 'Australia/Perth', labelKey: 'settings:account.timezone.zones.perth' },
      { value: 'Australia/Adelaide', labelKey: 'settings:account.timezone.zones.adelaide' },
      { value: 'Australia/Sydney', labelKey: 'settings:account.timezone.zones.sydney' },
      { value: 'Pacific/Auckland', labelKey: 'settings:account.timezone.zones.auckland' },
      { value: 'Pacific/Fiji', labelKey: 'settings:account.timezone.zones.fiji' },
      { value: 'Pacific/Honolulu', labelKey: 'settings:account.timezone.zones.honoluluAlsoUs' },
    ],
  },
];

/**
 * #2741: the Select's value for "no zone chosen". Radix Select cannot hold an
 * empty-string item, so this stands in for the stored empty setting, which
 * means "follow the browser".
 */
export const BROWSER_ZONE_VALUE = '__browser__';
